import { useState } from 'react';

import { useGetFormalOffer, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { Button, Spinner } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './Success.module.css';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { SuccessCircleSVG } from '../../../../svgComponents/successCircleSVG';
import { getDrawdownType } from '../../../drawDown/getDrawdownType';
import { DRAW_DOWN_MODE } from '../../../funding/ConfirmFundingView/ConfirmFunding.sagas';
import FallbackErrorModal from '../../fallbackErrorModal/FallbackErrorModal';
import { Content } from '../Content';
import { PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD } from '../ProcessingPaymentModal.actions';
import { CreditCardWaitlistInterstitial } from './CreditCardWaitlistInterstitial/CreditCardWaitlistInterstitial';

const mapStateToProps = (state: RootState) => ({
    ...state.processingPaymentModal
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToHome: () =>
        dispatch({ type: PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const Success = ({
    goToHome,
    senderLogo,
    receiverLogo,
    mode,
    offerId,
    amount
}: PropsFromRedux & {
    senderLogo: string;
    receiverLogo: string;
}) => {
    const [isInOfferFlow, setIsInOfferFlow] = useState(false);

    const showInterstitial =
        useSplitTests('cc_waitlist_interstitial') &&
        mode === DRAW_DOWN_MODE &&
        !!offerId;

    return (
        <>
            {!isInOfferFlow && (
                <Content
                    title={<SuccessTitle />}
                    icon={
                        <span className={styles.processingDirectionSuccess}>
                            <SuccessCircleSVG
                                className={styles.SuccessCircleSVG}
                                data-testid="success-payment-icon"
                            />
                        </span>
                    }
                    senderLogo={senderLogo}
                    receiverLogo={receiverLogo}
                    ctaOnClick={showInterstitial ? undefined : goToHome}
                    ctaText={
                        showInterstitial ? undefined : (
                            <FormattedMessage
                                id="_.navigation.return_to_dashboard"
                                defaultMessage="Return to dashboard"
                            />
                        )
                    }
                />
            )}
            {showInterstitial && (
                <ErrorBoundary
                    fallback={() => (
                        <div className="mt-l text-center">
                            <Button variant="action" onClick={goToHome}>
                                <FormattedMessage
                                    id="_.navigation.return_to_dashboard"
                                    defaultMessage="Return to dashboard"
                                />
                            </Button>
                        </div>
                    )}
                >
                    <CreditCardWaitlistInterstitial
                        offerId={offerId}
                        amount={amount!}
                        goToHome={goToHome}
                        onEnterOfferFlow={() => setIsInOfferFlow(true)}
                    />
                </ErrorBoundary>
            )}
        </>
    );
};

const SuccessTitle = connect(mapStateToProps)(({
    mode,
    amount,
    paymentMethod,
    offerId
}: ReturnType<typeof mapStateToProps>) => {
    const formattedAmount = <FormattedCurrency value={amount!} />;

    if (mode !== DRAW_DOWN_MODE) {
        return (
            <>
                <p>
                    <FormattedMessage
                        id="_.processing_payment.modal.early_repayment.confirmed"
                        defaultMessage="{amount} has been repaid by your debit card ending {paymentMethod}"
                        values={{
                            amount: formattedAmount,
                            paymentMethod: paymentMethod
                        }}
                    />
                </p>
                <p className={styles.subtitle}>
                    <FormattedMessage
                        id="_.processing_payment.modal.early_repayment.subtitle"
                        defaultMessage="Your outstanding balance may take a moment to update"
                    />
                </p>
            </>
        );
    }

    return (
        <DrawdownLoading formattedAmount={formattedAmount} offerId={offerId!} />
    );
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Success);

const DrawdownLoading = ({
    formattedAmount,
    offerId
}: {
    formattedAmount: React.ReactNode;
    offerId: string;
}) => {
    const { formalOffer, loadingFormalOffer, formalOfferError } =
        useGetFormalOffer(offerId);

    if (loadingFormalOffer) return <Spinner />;
    if (formalOfferError) return <FallbackErrorModal />;

    const drawdownType = getDrawdownType(formalOffer!);

    switch (drawdownType) {
        case 'revolving_credit_facility_cashless_activation':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.cashless_activation.confirmed"
                    defaultMessage="Your credit line is now active"
                />
            );
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.repayment_reduction.confirmed"
                    defaultMessage="You have successfully reduced your monthly repayments"
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.interest_only_extension.confirmed"
                    defaultMessage="We've extended your interest-only period"
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.draw_down.confirmed"
                    defaultMessage="We've sent {amount} to your bank account"
                    values={{ amount: formattedAmount }}
                />
            );
    }
};
